import { Store } from 'laco'
import EncountersService from 'Services/encounters'

import { SORTINGTYPES, TCODEVIEW, ENCOUNTERVIEW } from 'Constants'
import { hashObject } from 'Helpers'

import { toast } from 'react-toastify'
import ReportService from 'Services/report'
import UserStore from './user'

const EncountersStore = new Store({
  encounters: [],
  selectedHl7Id: undefined,
  sortingParams: {
    type: SORTINGTYPES.DATE,
    reverse: false
  },
  filteringParams: {},
  searchParams: {},
  loaded: false,
  viewType: ENCOUNTERVIEW,
  reportKeys: []
})

let isLoadingNextEncounters = false

export const updateEncounters = async () => {
  if (isLoadingNextEncounters) return
  isLoadingNextEncounters = true

  const { searchingParams, sortingParams, filteringParams, searchParams } = EncountersStore.get()
  const { virtualOffices } = UserStore.get()

  EncountersStore.set(() => ({ loaded: false }))
  const encounters = await EncountersService.list({
    virtualOfficeId: virtualOffices[0]?.id,
    searchingParams,
    sortingParams,
    filteringParams,
    searchParams
  }).catch(err => {
    toast.error(`Failed to load encounters${err.message ? `: ${err.message}` : ''}`)
  })

  isLoadingNextEncounters = false

  if (!encounters) return
  EncountersStore.set(() => ({ encounters, loaded: true }))
}

export const loadNextEncounters = async () => {
  if (isLoadingNextEncounters) return
  isLoadingNextEncounters = true

  const {
    searchingParams,
    sortingParams,
    filteringParams,
    searchParams,
    encounters
  } = EncountersStore.get()
  const { virtualOffices } = UserStore.get()

  if (!virtualOffices) {
    isLoadingNextEncounters = false
    return
  }

  const newEncounters = await EncountersService.list({
    virtualOfficeId: virtualOffices[0]?.id,
    searchingParams,
    sortingParams,
    filteringParams,
    searchParams,
    skip: encounters.length
  }).catch(err => {
    toast.error(`Failed to load new encounters${err.message ? `: ${err.message}` : ''}`)
  })

  isLoadingNextEncounters = false

  if (!newEncounters) return
  // TODO: Sort the new array based on sorting params, on backend?
  encounters.push(...newEncounters)
  EncountersStore.set(() => ({ encounters }))
}

export const updateEncounter = async (encounterId, operationType) => {
  const { virtualOffices } = UserStore.get()

  EncountersStore.set(() => ({ loaded: false }))

  const targetEncounters = await EncountersService.list({
    virtualOfficeId: virtualOffices[0]?.id,
    searchParams: {
      searchParam: {
        keys: ['EncounterId'],
        values: [encounterId]
      }
    }
  })

  if (targetEncounters.length > 0) {
    const targetEncounter = targetEncounters[0]
    const { encounters } = EncountersStore.get()
    let newEncounters

    /* Always check if its an insert */
    if (operationType !== 'insert') {
      // update a single encounter in the encounter list
      newEncounters = encounters.map(encounter => {
        if (encounter.id === targetEncounter.id) {
          encounter = targetEncounter
        }

        return encounter
      })
      EncountersStore.set(() => ({ encounters: newEncounters }))
    } else {
      await updateEncounters()
    }
  } else {
    /* This happens normally when an encounter is deleted */
    await updateEncounters()
  }

  EncountersStore.set(() => ({ loaded: true }))
}

export const setSelection = hl7Id => {
  const { selectedHl7Id } = EncountersStore.get()

  if (selectedHl7Id === hl7Id) return

  if (typeof hl7Id !== 'number' && hl7Id !== undefined) {
    console.error('The encounter selection must be undefined or a number')
    return
  }
  EncountersStore.set(() => ({ selectedHl7Id: hl7Id, viewType: ENCOUNTERVIEW }))
}

export const setSortingParams = async ({ type, reverse }) => {
  const sortingParams = {}
  if (typeof type === 'number') sortingParams.type = type
  if (typeof reverse === 'boolean') sortingParams.reverse = reverse
  EncountersStore.set(state => ({ sortingParams: { ...state.sortingParams, ...sortingParams } }))
  await updateEncounters()
}

export const setFilteringParams = async ({ type, clear, ...args }) => {
  const filteringParams = {}
  if (typeof type === 'string') filteringParams.type = type
  EncountersStore.set(state => ({
    filteringParams: { ...state.filteringParams, ...filteringParams, ...args }
  }))
  await updateEncounters()
}

export const clearFilteringParams = async () => {
  EncountersStore.set(() => ({ filteringParams: {} }))
  await updateEncounters()
}

export const clearSearchParams = async () => {
  EncountersStore.set(() => ({ searchParams: {} }))
  await updateEncounters()
}

export const getSearchParam = () => {
  const { searchParams } = EncountersStore.get()

  return searchParams
}

export const setFilteringSearchParams = async ({
  type,
  clear,
  keys,
  values,
  filteringParam,
  ...args
}) => {
  // Get previous search params
  let { searchParam } = getSearchParam()
  const { filteringParams } = EncountersStore.get()

  // If no previous search params, set the initial one
  if (!searchParam) {
    searchParam = { keys: [], values: [] }
  }

  // Do not process search when search and filtering params are all the same
  if (
    hashObject(searchParam.keys) === hashObject(keys) &&
    hashObject(searchParam.values) === hashObject(values) &&
    hashObject(filteringParams) === hashObject(filteringParam)
  ) {
    return
  }

  // Update our searchParam keys
  searchParam.keys = keys
  searchParam.values = values

  EncountersStore.set(state => ({
    searchParams: { ...state.searchParams, searchParam, ...args }
  }))

  if (typeof type === 'string') filteringParam.type = type

  EncountersStore.set(state => ({
    filteringParams: { ...state.filteringParams, ...filteringParam, ...args }
  }))

  if (keys.length === 0 && values.length === 0) {
    return clearSearchParams()
  }

  await updateEncounters()
}

export const clearFilteringSearchParams = async () => {
  EncountersStore.set(() => ({ searchParams: {} }))
  EncountersStore.set(() => ({ filteringParams: {} }))
  await updateEncounters()
}

export const setSearchParams = async ({ keys, values, ...args }) => {
  if (keys.length === 0 && values.length === 0) {
    return clearSearchParams()
  }

  // Get previous search params
  let { searchParam } = getSearchParam()

  // If there is previous search params
  if (searchParam) {
    // Check if the key is the same
    if (searchParam.keys.includes(keys[0])) {
      const indx = searchParam.keys.indexOf(keys[0])

      // If the value is also the same, just trying to "refresh" the search
      if (searchParam.values[indx] === values[0]) return

      // Can't use destructuring due to scope
      // eslint-disable-next-line prefer-destructuring
      searchParam.values[indx] = values[0]
    } else {
      // Different search key
      searchParam.keys = [].concat(searchParam.keys, keys)
      searchParam.values = [].concat(searchParam.values, values)
    }
  }

  // If no previous search params, set the initial one
  if (!searchParam) searchParam = { keys, values }

  EncountersStore.set(state => ({
    searchParams: { ...state.searchParams, searchParam, ...args }
  }))

  await updateEncounters()
}

export const setTcodeViewType = () => {
  EncountersStore.set(() => ({ viewType: TCODEVIEW }))
}

export const setEncounterViewType = () => {
  EncountersStore.set(() => ({ viewType: ENCOUNTERVIEW }))
}

export const createCSV = async ({ sortingParams, filteringParams, searchParams, heading }) => {
  const resp = await ReportService.create({ sortingParams, filteringParams, searchParams, heading })
  const { reportKeys } = EncountersStore.get()
  reportKeys.push(resp.key)
  EncountersStore.set(() => ({ reportKeys }))
}

export const removeKey = async ({ key }) => {
  let { reportKeys } = EncountersStore.get()

  reportKeys = reportKeys.filter(val => {
    return val !== key
  })

  EncountersStore.set(() => ({ reportKeys }))
}

export const checkKey = ({ key }) => {
  const { reportKeys } = EncountersStore.get()
  const check = reportKeys.includes(key)
  return check
}

export const clearStore = () => {
  EncountersStore.reset()
}

export default EncountersStore
